/**
 * この端末から出したデータ削除申請の ID を覚えておく（課題 #93）。
 *
 * 本人が誤って出した申請を取り消せるようにしたいが、BE には「自分の申請の一覧」を
 * 返す API が無い（ID を指定した取得と撤回だけ）。申請 ID を入力させる導線は
 * 作らない方針（他人の申請を指定できる余地を画面に残さない）なので、
 * この端末から出した申請の ID だけを利用者ごとに保存し、状態は毎回 API から取り直す。
 *
 * ほかの端末から出した申請はここに出ない。その取り消しは管理者の撤回で対応する。
 * 保存領域が使えない環境では何も覚えず、画面はその場で出した申請だけを扱う。
 */

/** 利用者ごとのキー。共有端末で別の人の申請が混ざらないようにする */
export const MY_DELETION_REQUESTS_KEY_PREFIX = 'ceeu.myDeletionRequests.';

/** 覚えておく件数の上限。古いものから捨てる */
export const MY_DELETION_REQUESTS_MAX = 10;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function keyFor(userId: string): string {
  return `${MY_DELETION_REQUESTS_KEY_PREFIX}${userId}`;
}

/** 覚えている申請 ID（新しい順）。読めないときは空 */
export function loadMyRequestIds(storage: StorageLike | null, userId: string): string[] {
  if (!storage || !userId) return [];
  try {
    const raw = storage.getItem(keyFor(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

function save(storage: StorageLike | null, userId: string, ids: string[]): void {
  if (!storage || !userId) return;
  try {
    storage.setItem(keyFor(userId), JSON.stringify(ids.slice(0, MY_DELETION_REQUESTS_MAX)));
  } catch {
    /* 保存できなくても、その場の表示には影響させない */
  }
}

/** 申請を出したときに先頭へ加える（重複は除く） */
export function rememberMyRequestId(storage: StorageLike | null, userId: string, id: string): void {
  const ids = loadMyRequestIds(storage, userId).filter((v) => v !== id);
  save(storage, userId, [id, ...ids]);
}

/** 見つからなくなった申請（404）を忘れる */
export function forgetMyRequestId(storage: StorageLike | null, userId: string, id: string): void {
  save(
    storage,
    userId,
    loadMyRequestIds(storage, userId).filter((v) => v !== id)
  );
}
